import { STATIC_PAGE_META, type PageMeta } from '@visualry/shared';

const DEFAULT_PUBLIC_APP_URL = 'https://visualry.ai';

const stripTrailingSlash = (path: string): string =>
  path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

/**
 * Resolve the canonical public origin used to build absolute URLs in
 * <SeoHead> (canonical link, og:url, og:image, etc.).
 *
 * Priority:
 * 1. `VITE_PUBLIC_APP_URL` build-time env var — set to the production
 *    domain so og:url / canonical point to visualry.ai even on the
 *    workers.dev preview deploy.
 * 2. `window.location.origin` at runtime — correct for local dev
 *    (`http://localhost:5173`) and for any same-origin SPA deployment.
 */
export const resolvePublicAppUrl = (): string => {
  const fromEnv = import.meta.env.VITE_PUBLIC_APP_URL;
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return DEFAULT_PUBLIC_APP_URL;
};

export type PageMetaLookup = {
  title: string;
  description: string;
  noindex?: boolean;
  ogImage?: string;
  path: string;
  jsonLd?: (publicAppUrl: string) => unknown[];
};

/**
 * Look up the matching PageMeta by path. Returns `null` when the path
 * is not in the static page registry (e.g. /prompt/:slug — those pages
 * compute their own meta from the prompt data).
 */
export const lookupPageMeta = (path: string): PageMetaLookup | null => {
  const normalized = stripTrailingSlash(path);
  const found: PageMeta | undefined = STATIC_PAGE_META.find((m) => m.path === normalized);
  if (!found) return null;
  return {
    title: found.title,
    description: found.description,
    noindex: found.noindex,
    ogImage: found.ogImage,
    path: found.path,
    jsonLd: found.jsonLd,
  };
};
